import React, { Fragment, useState, useEffect } from 'react';
import { Form, Input, Button, Tooltip, Switch, Flex } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import CountrySelect from '../countries/components/CountrySelect';
import { useAuthSettingUtils } from '../../helpers/settings/settingUtils';

const Addresses = ({ 
  name = "addresses",
  initialValues = null,
  onChange = null,
  showAsFormList = true ,
  multiple = false,
  allowCountryChange = true,  // New prop to control if country can be changed
  /** When false, Address Line 1 is not required (e.g. customer form). */
  requireAddressLine1 = true,
}) => {
  const [defaultField, setDefaultField] = useState(0);
  const initializedRef = React.useRef(false);
  const form = Form.useFormInstance();
  const { getAuthSetting, getAuthOrganization } = useAuthSettingUtils();

  // Initialize form values only once to prevent re-runs caused by parent re-renders
  // creating a new `initialValues` array reference (e.g. during setSaving).
  useEffect(() => {
    if (initializedRef.current) return;

    const currentValues = form.getFieldValue(name);

    if (initialValues && Array.isArray(initialValues) && initialValues.length > 0) {
      const processedValues = initialValues.map((address, index) => ({
        ...address,
        is_default: address.is_default !== undefined ? address.is_default : index === 0,
      }));
      form.setFieldsValue({ [name]: processedValues });
      initializedRef.current = true;
    } else if (!currentValues || !Array.isArray(currentValues) || currentValues.length === 0) {
      form.setFieldsValue({ [name]: getInitialValue() });
      initializedRef.current = true;
    }
  }, [name, form, initialValues]);

  const handleDefaultChange = (fieldIndex) => {
    setDefaultField(fieldIndex);
    
    // Update all other fields' is_default to false (current field is handled by Form.Item)
    const currentValues = form.getFieldValue(name) || [];
    currentValues.forEach((item, index) => {
      if (index !== fieldIndex) {
        form.setFieldValue([name, index, 'is_default'], false);
      }
    });
  };

  const handleCountryChange = (value, fieldName) => {
    const currentValues = form.getFieldValue(name) || [];
    const updatedValues = [...currentValues];
    
    if (updatedValues[fieldName]) {
      updatedValues[fieldName] = {
        ...updatedValues[fieldName],
        country_id: value.country_id,
        state_id: value.state_id,
      };

      form.setFieldsValue({
        [name]: updatedValues
      });
    }
  };



  const getDefaultLocation = () => {
    const organization = getAuthOrganization?.() || {};

    return {
      country_id: organization?.country?.id || getAuthSetting('country_id') || null,
      state_id: organization?.state?.id || getAuthSetting('state_id') || null,
    };
  };

  const getInitialValue = () => {
    const defaultLocation = getDefaultLocation();

    // Always return at least one default address if no initial values
    return [{
      ...defaultLocation,
      address_line_1: '',
      address_line_2: '',
      postal_code: '',
      is_default: true
    }];
  };

  // if (!showAsFormList && onChange) {
  //   return null;
  // }

  return (
    <Form.List 
      name={name}
    >

      {(fields, { add, remove }) => (
        <Fragment>
          {fields.map((field, index) => {
            const currentValues  = form.getFieldValue(name) || [];
            const currentAddress = currentValues[index] || {};
            const isDefault      = currentValues[index]?.is_default || false;

            return (
              <div key={`address-${field.key}-${index}`} className="">
                <Flex justify="space-between" className="mb-3">
                  {multiple && 
                  <Flex align="flex-end" justify="space-between">
                    <div style={{width: '120px'}}>
                      {isDefault ? <small className="text-muted me-2">Address {index + 1} is default</small> : <small className="text-muted me-2">Set address {index + 1} as default</small>}
                    </div>
                    
                    <Form.Item
                      name={[field.name, 'is_default']}
                      noStyle
                      valuePropName="checked"
                    >
                      <Switch 
                        size="small"
                        onChange={(checked) => {
                          if (checked) {
                            handleDefaultChange(index);
                          }
                        }}
                      />
                    </Form.Item>
                  </Flex>}
                  <div>
                    {fields.length > 1 && (
                      <Tooltip title="Remove address">
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            remove(field.name);
                            if (index === defaultField && fields.length > 1) {
                              setDefaultField(0);
                            } else if (index < defaultField) {
                              setDefaultField(defaultField - 1);
                            }
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </Flex>

                {/* Hidden Form.Item for is_default when multiple is false to ensure it's tracked by the form */}
                {!multiple && (
                  <Form.Item
                    name={[field.name, 'is_default']}
                    noStyle
                    initialValue={true}
                    getValueFromEvent={() => true}
                    normalize={() => true}
                  >
                    <Input type="hidden" value="true" />
                  </Form.Item>
                )}

                <Form.Item label="Location" style={{ marginBottom: '16px' }}>
                  <CountrySelect
                    selectedCountry={currentAddress.country_id}
                    selectedState={currentAddress.state_id}
                    onChange={(value) => handleCountryChange(value, index)}
                    disableCountry={!allowCountryChange}
                  />
                  <Form.Item name={[field.name, 'country_id']} noStyle>
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item name={[field.name, 'state_id']} noStyle>
                    <Input type="hidden" />
                  </Form.Item>
                </Form.Item>

                <Form.Item
                  name={[field.name, 'address_line_1']}
                  label="Address Line 1"
                  rules={requireAddressLine1 ? [{ required: true, message: 'Address Line 1 is required' }] : []}
                >
                  <Input placeholder={requireAddressLine1 ? 'Enter address line 1' : 'Enter address line 1 (optional)'} />
                </Form.Item>

                <Form.Item
                  name={[field.name, 'address_line_2']}
                  label="Address Line 2"
                >
                  <Input placeholder="Enter address line 2 (optional)" />
                </Form.Item>

                <Form.Item
                  name={[field.name, 'postal_code']}
                  label="Postal Code"
                >
                  <Input placeholder="Enter postal code" />
                </Form.Item>
              </div>
            );
          })}

          {multiple && <Form.Item>
            <Button
              type="link"
              size='small'
              onClick={() => add({
                ...getDefaultLocation(),
                address_line_1: '',
                address_line_2: '',
                postal_code: '',
                is_default: fields.length === 0 // Set as default if it's the first address
              })}
              icon={<PlusOutlined />}
              className='px-0'
            >
              Add Address
            </Button>
          </Form.Item>}
        </Fragment>
      )}
    </Form.List>
  );
};

export default Addresses;





